import type { OrderLineItemSchema, OrderResponseSchema } from '@lyka-pet-food/contracts'
import { AddressState, DeliverySlotWindow } from '@lyka-pet-food/contracts'
import { z } from 'zod'

export type Order = z.infer<typeof OrderResponseSchema>
export type OrderLineItem = z.infer<typeof OrderLineItemSchema>

export const OrderDisplayType = {
  HIDDEN: 'HIDDEN',
  FAILED: 'FAILED',
  PAST: 'PAST',
  CURRENT_PREPARING: 'PREPARING',
  CURRENT_ON_ITS_WAY: 'ON ITS WAY',
  CURRENT_DELIVERED: 'DELIVERED',
  FUTURE: 'FUTURE',
  NEXT_AVAILABLE: 'NEXT AVAILABLE',
} as const
export type OrderDisplayType = (typeof OrderDisplayType)[keyof typeof OrderDisplayType]

export interface OrderFeedingPlan {
  animalId: string
  planType: string
  skus: OrderLineItem[]
}

// A line item with its total/discountedTotal already resolved by the BFF (see
// getLineItemTotal/getLineItemDiscountedTotal in order.utils.ts). These read ONLY
// totalPrice/totalCharge - never recomputed from price/quantity/discountedPrice - so
// they're null whenever the backend hasn't computed a trustworthy value for this line
// item (mainly very old orders predating that field). Callers must handle null (e.g.
// render '-' or hide the row) rather than inventing a number.
export type EnrichedOrderLineItem = OrderLineItem & {
  total: number | null
  discountedTotal: number | null
}

export interface OrderPaymentSummaryCategory {
  // null means the backend hasn't provided a trustworthy value for this category (see
  // order.utils.ts) - never a computed guess. Callers should hide the row/section
  // rather than render a fabricated number.
  total: number | null
  discountedTotal: number | null
  discountAmount: number | null
  // The discount that actually won pricing for every line item in this category, if
  // they all agree on one. Resolved from appliedDiscountId via the pricing service's
  // discounts catalog - never derived from marketing copy or a hardcoded label, since
  // more than one discount (e.g. a staff discount vs. the multi-treat promo) can be
  // eligible for the same line item and only one of them actually wins.
  appliedDiscount?: {
    id: string
    name: string
    percentage?: number
  }
}

export interface OrderPaymentSummary {
  allLykaPlan: OrderPaymentSummaryCategory
  treats: OrderPaymentSummaryCategory
  supplements: OrderPaymentSummaryCategory
  total: OrderPaymentSummaryCategory
  paymentMethod?: {
    brand?: string
    last4Digits?: string
  }
}

/**
 * Order as returned by the CA BFF after enrichment.
 * Display fields below are calendar-day facts in `address.timezone` — use them for UI copy.
 */
export type EnrichedOrder = Order & {
  displayType: OrderDisplayType
  isDeliveryToday: boolean
  isPaymentToday: boolean
  /** Calendar days from today to deliveryDate in address.timezone. 0 = today, negative = past. */
  daysUntilDelivery: number
  /** Calendar days from today to billingDate in address.timezone. 0 = today, negative = past. */
  daysUntilPayment: number
  /** End of billingDate (23:59:59) in address.timezone, ISO with offset. */
  editCutoffAt: string
  /** YYYY-MM-DD for deliveryDate in address.timezone. */
  deliveryDateLocal: string
  /** YYYY-MM-DD for billingDate in address.timezone. */
  billingDateLocal: string
  /** YYYY-MM-DD for billedDate in address.timezone. Null when the order has not been charged yet. */
  billedDateLocal: string | null
  feedingPlans: OrderFeedingPlan[]
  extras: EnrichedOrderLineItem[]
  chargedAmount: number
  isMultiTreatDiscountApplied: boolean
  paymentSummary: OrderPaymentSummary
}

export const UpdateOrderDeliveryInfoSchema = z.object({
  applyToFutureOrders: z.boolean().optional(),
  postcode: z.string(),
  suburb: z.string(),
  address: z
    .object({
      addressId: z.string(),
      address1: z.string(),
      address2: z.string().optional(),
      city: z.string(),
      countryCode: z.string(),
      state: z.nativeEnum(AddressState),
      companyName: z.string().optional(),
      deliveryInstructions: z.string().optional(),
    })
    .optional(),
  date: z
    .object({
      deliveryDate: z
        .string()
        .refine((val) => /^\d{4}-\d{2}-\d{2}$/.test(val), { message: 'deliveryDate must be in YYYY-MM-DD format' }),
      deliverySlotId: z.nativeEnum(DeliverySlotWindow),
    })
    .optional(),
  deliveryContactNumber: z.string().optional().nullable(),
})

export type UpdateOrderDeliveryInfo = z.infer<typeof UpdateOrderDeliveryInfoSchema>
